// KanbanV2でのみ使用される進捗アイコンコンポーネント

import {
  HourglassEmptyRounded,
  CheckCircleOutlineRounded,
} from '@mui/icons-material'
import { Box, Tooltip as MuiTooltip, Typography, styled } from '@mui/material'
import { memo, useMemo } from 'react'
import { Color, FontSize } from '../../../../../../../../styles/commonStyles'
import { WbsItemRow } from '../../../../../../../../lib/functions/wbsItem'
import { dateV2Service } from '../../../../../../../../domain/value-object/DateV2'
import { colorPalette } from '../../../../../../../style/colorPalette'

import { WbsItemStatus } from '.'
import WbsItemRowApi from '../../../../lib/wbsItem/index'
import SmallFireIcon from '../../../../../../../components/icons/SmallFireIcon'
import BigFireIcon from '../../../../../../../components/icons/BigFireIcon'
import CoinIcon from '../../../../../../../components/icons/CoinIcon'
import { intl } from '../../../../../../../../i18n'

const isNotStarted = (wbsItem: Partial<WbsItemRow>): boolean => {
  const isTodoOrDiscard = [WbsItemStatus.TODO, WbsItemStatus.DISCARD].includes(
    wbsItem.status!
  )
  if (!isTodoOrDiscard) return false

  const { scheduledDate } = wbsItem
  // 開始日も終了日もない
  if (!scheduledDate?.startDate && !scheduledDate?.endDate) {
    return true
  }
  // 今日が開始予定日より前（開始予定日当日は含まない）
  const startDate = dateV2Service.fromString(scheduledDate?.startDate)
  const isBeforeStart = startDate
    ? dateV2Service.today().getTime() < startDate.getTime()
    : false

  return isBeforeStart
}

// ツールチップのスタイル（UserAvatarsGroupと同様）
const tooltipSlotProps = {
  popper: {
    sx: {
      filter: 'drop-shadow(0px 2px 6px rgba(123, 140, 170, 0.4))',
    },
  },
  tooltip: {
    sx: {
      backgroundColor: colorPalette.monotone[0],
      color: colorPalette.monotone[10],
      fontSize: '12px',
      padding: '8px 12px',
      borderRadius: '4px',
      boxShadow: 'none',
    },
  },
  arrow: {
    sx: {
      color: colorPalette.monotone[0],
    },
  },
}

const TooltipRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  height: '15px',
})

const StatusLabel = styled(Typography)({
  fontSize: '12px',
  fontWeight: 'bold',
})

const formatDate = (date: string | undefined): string => {
  if (!date) return '-'
  const dateV2 = dateV2Service.fromString(date)
  if (!dateV2) return '-'
  return dateV2Service.formatMonthAndDate(dateV2)
}

const formatHour = (hour: number | undefined): string => {
  if (hour === undefined || hour === null) return '-'
  const unit = intl.formatMessage({ id: 'workloadUnit.hour' })
  return `${hour}${unit}`
}

const formatDateRange = (
  startDate: string | undefined,
  endDate: string | undefined
): string => {
  const start = formatDate(startDate)
  const end = formatDate(endDate)
  return `${start} ～ ${end}`
}

type TooltipContentProps = {
  wbsItem: Partial<WbsItemRow>
  statusMessage: string
}

const TooltipContent = memo(
  ({ wbsItem, statusMessage }: TooltipContentProps) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <StatusLabel>{statusMessage}</StatusLabel>
        <TooltipRow>
          <Typography component="span" sx={{ fontSize: '12px' }}>
            {formatDateRange(
              wbsItem.scheduledDate?.startDate,
              wbsItem.scheduledDate?.endDate
            )}
          </Typography>
        </TooltipRow>
        <TooltipRow>
          <Typography component="span" sx={{ fontSize: '12px' }}>
            {intl.formatMessage(
              { id: 'wbs.tooltip.workload' },
              {
                actual: formatHour(wbsItem.actualHour),
                estimated: formatHour(wbsItem.estimatedWorkload?.hour),
              }
            )}
          </Typography>
        </TooltipRow>
      </Box>
    )
  }
)

type ProgressIconProps = {
  wbsItem: Partial<WbsItemRow>
  statusMessage: string
  children: React.ReactElement
}

const ProgressIconWithTooltip = memo(
  ({ wbsItem, statusMessage, children }: ProgressIconProps) => {
    const tooltipContent = useMemo(
      () => <TooltipContent wbsItem={wbsItem} statusMessage={statusMessage} />,
      [wbsItem, statusMessage]
    )

    return (
      <MuiTooltip
        title={tooltipContent}
        placement="top"
        arrow={true}
        slotProps={tooltipSlotProps}
      >
        {children}
      </MuiTooltip>
    )
  }
)

export const KanbanProgressIcon = (props: {
  wbsItem: Partial<WbsItemRow>
  fontSize?: number
}) => {
  const { wbsItem, fontSize } = props
  if (!wbsItem || !wbsItem.status) return <></>
  const todo = wbsItem.status === WbsItemStatus.TODO
  const completed = [WbsItemStatus.DONE, WbsItemStatus.DISCARD].includes(
    wbsItem.status
  )
  if (WbsItemRowApi.isPrecedingOnSchedule(wbsItem)) {
    return (
      <ProgressIconWithTooltip
        wbsItem={wbsItem}
        statusMessage={intl.formatMessage({ id: 'wbs.preceding.on.schedule' })}
      >
        <CoinIcon
          style={{
            color: todo ? Color.ALERT : Color.DISABLED,
            fontSize: fontSize || FontSize.X_LARGE,
          }}
        />
      </ProgressIconWithTooltip>
    )
  } else if (WbsItemRowApi.isEndDelayed(wbsItem)) {
    return (
      <ProgressIconWithTooltip
        wbsItem={wbsItem}
        statusMessage={intl.formatMessage({ id: 'wbs.end.delayed' })}
      >
        <BigFireIcon
          style={{
            color: completed ? Color.DISABLED : undefined,
            fontSize: fontSize || FontSize.X_LARGE,
          }}
        />
      </ProgressIconWithTooltip>
    )
  } else if (WbsItemRowApi.isStartDelayed(wbsItem)) {
    return (
      <ProgressIconWithTooltip
        wbsItem={wbsItem}
        statusMessage={intl.formatMessage({ id: 'wbs.start.delayed' })}
      >
        <SmallFireIcon
          style={{
            color: todo ? undefined : Color.DISABLED,
            fontSize: fontSize || FontSize.X_LARGE,
          }}
        />
      </ProgressIconWithTooltip>
    )
  } else if (isNotStarted(wbsItem)) {
    return (
      <ProgressIconWithTooltip
        wbsItem={wbsItem}
        statusMessage={intl.formatMessage({ id: 'wbs.not.started' })}
      >
        <HourglassEmptyRounded
          style={{
            color: Color.DISABLED,
            fontSize: fontSize || FontSize.X_LARGE,
          }}
        />
      </ProgressIconWithTooltip>
    )
  }
  // フォールバック: 予定通り進行中
  return (
    <ProgressIconWithTooltip
      wbsItem={wbsItem}
      statusMessage={intl.formatMessage({ id: 'wbs.on.schedule.progress' })}
    >
      <CheckCircleOutlineRounded
        style={{
          color: Color.MAIN,
          fontSize: fontSize || FontSize.X_LARGE,
        }}
      />
    </ProgressIconWithTooltip>
  )
}
